import { useState, useRef, useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { useEpisode } from '@/hooks'
import {
  useQuizByEpisode,
  useQuizAttempt,
  useSaveAnswer,
  useSubmitQuizAttempt,
} from '@/services/admin/quiz.service'
import {
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Trophy,
  RotateCcw,
  BookOpen,
  Star,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'

type Answer = { questionId: number; selectedOptionId: number; correct: boolean; timeSpent: number }

const Quiz = () => {
  const { moduleId, episodeId } = useParams()
  const navigate = useNavigate()
  const modId = parseInt(moduleId || '1')
  const epId = parseInt(episodeId || '1')

  const { data: episodeData, isLoading: episodeLoading } = useEpisode(modId, epId)
  const { data: quiz, isLoading: quizLoading } = useQuizByEpisode(episodeData?.episode?.id)
  const { data: previousAttempt, isLoading: attemptLoading } = useQuizAttempt(quiz?.id)

  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [answers, setAnswers] = useState<Answer[]>([])
  const [finished, setFinished] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [direction, setDirection] = useState<1 | -1>(1)
  const [startedNewAttempt, setStartedNewAttempt] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const saveAnswer = useSaveAnswer()
  const submitAttempt = useSubmitQuizAttempt()
  const questionStartTime = useRef<number>(Date.now())

  const questions = useMemo(() => quiz?.questions ?? [], [quiz])
  const total = questions.length

  // Restore in_progress attempt from backend (only once on mount)
  useEffect(() => {
    if (initialized || attemptLoading || !previousAttempt) return
    if (startedNewAttempt) return
    if (answers.length > 0) {
      setInitialized(true)
      return
    }

    if (previousAttempt.status === 'in_progress' && Array.isArray(previousAttempt.answers)) {
      // Backend retorna { questionId, selectedOptionId, isCorrect, timeSpent }.
      // O front usa o campo `correct` no lugar de `isCorrect`.
      type BackendAnswer = {
        questionId: number
        selectedOptionId: number
        isCorrect: boolean
        timeSpent?: number
      }
      // Reconcilia as respostas salvas contra as perguntas ATUAIS do quiz,
      // alinhando por questionId. A tentativa in_progress pode conter respostas
      // para perguntas que não existem mais (quiz editado depois de iniciada) ou
      // para TODAS as perguntas (respondeu tudo mas não submeteu). Antes, confiar
      // em `restored.length` jogava currentIndex pra fora do array (questions[total]
      // === undefined) e travava a tela com "N+1 / N" e o botão Confirmar morto.
      // Mantemos só o prefixo contíguo de respostas, pois o quiz é linear.
      const byQuestionId = new Map<number, BackendAnswer>(
        (previousAttempt.answers as BackendAnswer[]).map((a) => [a.questionId, a])
      )
      const restored: Answer[] = []
      for (let i = 0; i < total; i++) {
        const a = byQuestionId.get(questions[i].id)
        if (!a) break
        restored.push({
          questionId: a.questionId,
          selectedOptionId: a.selectedOptionId,
          correct: a.isCorrect,
          timeSpent: a.timeSpent ?? 0,
        })
      }

      if (restored.length === 0) {
        setInitialized(true)
        return
      }

      // Próxima pergunta sem resposta; se respondeu todas, fica na última em modo
      // revisão (o handleNext submete ao "Avançar"). Nunca passa de total - 1.
      const resumeIndex = Math.min(restored.length, total - 1)
      setAnswers(restored)
      setCurrentIndex(resumeIndex)
      setInitialized(true)
    }
  }, [
    previousAttempt,
    attemptLoading,
    initialized,
    startedNewAttempt,
    answers.length,
    total,
    questions,
  ])

  // Reset timer when question changes
  useEffect(() => {
    questionStartTime.current = Date.now()
  }, [currentIndex])
  const current = questions[currentIndex]
  const progress = total > 0 ? (currentIndex / total) * 100 : 0
  const isReviewing = currentIndex < answers.length && !confirmed

  // Show previous completed result
  const showPreviousResult =
    !startedNewAttempt &&
    !finished &&
    previousAttempt?.status === 'completed' &&
    answers.length === 0

  const handleConfirm = () => {
    if (selectedOption === null || !current) return
    const chosen = current.options.find((o) => o.id === selectedOption)
    const timeSpent = Date.now() - questionStartTime.current
    const isCorrect = !!chosen?.isCorrect

    const answer: Answer = {
      questionId: current.id,
      selectedOptionId: selectedOption,
      correct: isCorrect,
      timeSpent,
    }
    setAnswers((prev) => {
      const updated = [...prev]
      updated[currentIndex] = answer
      return updated
    })
    setConfirmed(true)

    // Save to backend immediately
    saveAnswer.mutate({
      quizId: quiz!.id,
      answer: {
        questionId: current.id,
        selectedOptionId: selectedOption,
        isCorrect,
        timeSpent,
      },
    })
  }

  const handleNext = () => {
    setDirection(1)
    const isLast = currentIndex + 1 >= total
    // Em revisão e ainda não é a última: só avança. Quando é a última (fluxo
    // normal OU revisão de uma tentativa já toda respondida), cai no submit
    // abaixo — antes a revisão incrementava pra fora do array e travava a tela.
    if (isReviewing && !isLast) {
      setCurrentIndex((i) => i + 1)
      return
    }
    if (isLast) {
      const finalAnswers = answers
      const finalCorrectCount = finalAnswers.filter((a) => a.correct).length
      const finalScore = total > 0 ? Math.round((finalCorrectCount / total) * 100) : 0

      setSubmitting(true)
      submitAttempt.mutate(
        {
          quizId: quiz!.id,
          data: {
            answers: finalAnswers.map((a) => ({
              questionId: a.questionId,
              selectedOptionId: a.selectedOptionId,
              isCorrect: a.correct,
              timeSpent: a.timeSpent,
            })),
            score: finalScore,
            correctAnswers: finalCorrectCount,
            totalQuestions: total,
          },
        },
        {
          onSettled: () => {
            setFinished(true)
            setSubmitting(false)
          },
        }
      )
    } else {
      setCurrentIndex((i) => i + 1)
      setSelectedOption(null)
      setConfirmed(false)
    }
  }

  const handleRestart = () => {
    setCurrentIndex(0)
    setSelectedOption(null)
    setConfirmed(false)
    setAnswers([])
    setFinished(false)
    setSubmitting(false)
    setStartedNewAttempt(true)
    setInitialized(false)
  }

  const handleBack = () => {
    if (currentIndex === 0) {
      navigate(`/episode/${modId}/${epId}`)
      return
    }
    setDirection(-1)
    setSelectedOption(null)
    setConfirmed(false)
    setCurrentIndex((i) => i - 1)
  }

  const getNextEpisodeLink = () => {
    if (episodeData?.navigation?.nextEpisode) {
      const { moduleId: nMod, number: nEp } = episodeData.navigation.nextEpisode
      return `/episode/${nMod}/${nEp}`
    }
    const courseId = episodeData?.episode?.module?.courseId
    return courseId ? `/course-feedback/${courseId}` : '/'
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (episodeLoading || quizLoading || attemptLoading || !episodeData) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 container flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    )
  }

  // ── Sem quiz ────────────────────────────────────────────────────────────
  if (!quiz || total === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 container flex items-center justify-center py-8">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Nenhum quiz disponível para este episódio.</p>
            <Link to={getNextEpisodeLink()} className="text-primary hover:underline">
              Continuar
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  // ── Result Screen (previous completed or just submitted) ────────────────
  const renderResultScreen = (
    resultScore: number,
    resultCorrect: number,
    resultTotal: number,
    title: string,
    subtitle?: string
  ) => {
    const medal = resultScore >= 80 ? '🏆' : resultScore >= 60 ? '🎯' : '📚'
    const message =
      resultScore >= 80
        ? 'Excelente! Você domina este conteúdo!'
        : resultScore >= 60
          ? 'Bom trabalho! Continue assim!'
          : 'Continue estudando — você vai chegar lá!'

    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 container flex items-center justify-center py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto text-center"
          >
            <div className="text-6xl mb-4">{medal}</div>
            <h1 className="text-3xl font-bold mb-2">{title}</h1>
            <p className="text-muted-foreground mb-2">{quiz.title}</p>
            {subtitle && <p className="text-xs text-muted-foreground mb-6">{subtitle}</p>}
            {!subtitle && <div className="mb-6" />}

            <div className="relative w-36 h-36 mx-auto mb-6">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="hsl(var(--muted))"
                  strokeWidth="3"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="3"
                  strokeDasharray={`${resultScore}, 100`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-primary">{resultScore}%</span>
                <span className="text-xs text-muted-foreground">
                  {resultCorrect}/{resultTotal}
                </span>
              </div>
            </div>

            <p className="text-foreground font-medium mb-8">{message}</p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" onClick={handleRestart} className="gap-2">
                <RotateCcw className="w-4 h-4" />
                Nova Tentativa
              </Button>
              <Button asChild className="gap-2">
                <Link to={getNextEpisodeLink()}>
                  {episodeData?.navigation?.nextEpisode ? (
                    <>
                      <BookOpen className="w-4 h-4" />
                      Próximo Episódio
                    </>
                  ) : (
                    <>
                      <Star className="w-4 h-4" />
                      Avaliar Curso
                    </>
                  )}
                </Link>
              </Button>
            </div>
          </motion.div>
        </main>
        <Footer />
      </div>
    )
  }

  if (showPreviousResult) {
    return renderResultScreen(
      previousAttempt.score,
      previousAttempt.correctAnswers,
      previousAttempt.totalQuestions,
      'Sua Última Tentativa',
      `Tentativa #${previousAttempt.attemptNumber} — ${new Date(previousAttempt.completedAt!).toLocaleDateString('pt-BR')}`
    )
  }

  if (finished) {
    const correctCount = answers.filter((a) => a.correct).length
    const score = total > 0 ? Math.round((correctCount / total) * 100) : 0
    return renderResultScreen(score, correctCount, total, 'Resultado do Quiz')
  }

  // ── Defensivo: índice fora do array ─────────────────────────────────────────
  // Não deve acontecer após os clamps do restore/handleNext, mas se acontecer
  // (dados inesperados, quiz editado etc.) NUNCA mostra a questão fantasma
  // travada do bug "N+1 / N" — conclui com o que já foi respondido.
  if (!current) {
    const correctCount = answers.filter((a) => a.correct).length
    const score = total > 0 ? Math.round((correctCount / total) * 100) : 0
    return renderResultScreen(score, correctCount, total, 'Resultado do Quiz')
  }

  // ── Question Screen ────────────────────────────────────────────────────────
  const reviewAnswer = isReviewing ? answers[currentIndex] : null
  const displaySelectedOption = isReviewing ? reviewAnswer!.selectedOptionId : selectedOption
  const displayConfirmed = isReviewing || confirmed
  const chosenOption = current?.options.find((o) => o.id === displaySelectedOption)
  const isCorrect = chosenOption?.isCorrect === true

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 container py-8 max-w-2xl mx-auto flex flex-col justify-center">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-lg font-medium text-foreground">{quiz.title}</span>
            <span className="text-sm text-muted-foreground">
              {currentIndex + 1} / {total}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 40 * direction }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 * direction }}
            transition={{ duration: 0.25 }}
            className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm"
          >
            <div className="flex items-start gap-3 mb-6">
              <span className="w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                {currentIndex + 1}
              </span>
              <p className="text-lg font-semibold text-foreground leading-relaxed">
                {current?.text}
              </p>
            </div>

            <div className="space-y-3">
              {current?.options.map((option) => {
                let variant: 'default' | 'correct' | 'wrong' | 'neutral' = 'default'
                if (displayConfirmed) {
                  if (option.isCorrect) variant = 'correct'
                  else if (option.id === displaySelectedOption) variant = 'wrong'
                  else variant = 'neutral'
                } else if (option.id === displaySelectedOption) {
                  variant = 'default'
                }

                const baseClass =
                  'w-full flex items-center gap-3 p-4 rounded-xl border text-left transition-all duration-150 focus:outline-none'
                const variantClass =
                  !displayConfirmed && option.id !== displaySelectedOption
                    ? 'border-border bg-muted/20 hover:border-primary/50 hover:bg-primary/5 cursor-pointer'
                    : !displayConfirmed && option.id === displaySelectedOption
                      ? 'border-primary bg-primary/10 cursor-pointer'
                      : variant === 'correct'
                        ? 'border-green-500/50 bg-green-500/10 cursor-default'
                        : variant === 'wrong'
                          ? 'border-destructive/50 bg-destructive/10 cursor-default'
                          : 'border-border bg-muted/10 opacity-60 cursor-default'

                return (
                  <button
                    key={option.id}
                    className={`${baseClass} ${variantClass}`}
                    onClick={() => !displayConfirmed && setSelectedOption(option.id)}
                    disabled={displayConfirmed}
                  >
                    <span
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 text-xs font-bold transition-colors ${
                        displayConfirmed && option.isCorrect
                          ? 'border-green-500 bg-green-500 text-white'
                          : displayConfirmed &&
                              option.id === displaySelectedOption &&
                              !option.isCorrect
                            ? 'border-destructive bg-destructive text-destructive-foreground'
                            : option.id === displaySelectedOption
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border'
                      }`}
                    >
                      {displayConfirmed && option.isCorrect ? (
                        <CheckCircle2 className="w-3 h-3" />
                      ) : displayConfirmed &&
                        option.id === displaySelectedOption &&
                        !option.isCorrect ? (
                        <XCircle className="w-3 h-3" />
                      ) : null}
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        displayConfirmed && option.isCorrect
                          ? 'text-green-400'
                          : displayConfirmed &&
                              option.id === displaySelectedOption &&
                              !option.isCorrect
                            ? 'text-red-400'
                            : 'text-foreground'
                      }`}
                    >
                      {option.text}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Actions */}
            <div className="mt-6 flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="w-4 h-4" />
                Voltar
              </Button>
              <div className="flex gap-3">
                {isReviewing ? (
                  <Button variant="secondary" onClick={handleNext} className="gap-2">
                    Avançar
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : !confirmed ? (
                  <Button
                    onClick={handleConfirm}
                    disabled={selectedOption === null}
                    className="gap-2"
                  >
                    Confirmar
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button onClick={handleNext} className="gap-2" disabled={submitting}>
                    {currentIndex + 1 >= total ? (
                      <>
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trophy className="w-4 h-4" />
                        )}
                        {submitting ? 'Enviando...' : 'Ver resultado'}
                      </>
                    ) : (
                      <>
                        Próxima questão
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Feedback Card */}
        <AnimatePresence>
          {displayConfirmed && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.2 }}
              className={`mt-4 flex items-center gap-3 p-4 rounded-2xl border ${
                isCorrect
                  ? 'bg-green-500/10 border-green-500/30'
                  : 'bg-destructive/10 border-destructive/30'
              }`}
            >
              <div className="flex items-start gap-3">
                {isCorrect ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p
                    className={`text-sm font-medium ${isCorrect ? 'text-green-400' : 'text-red-400'}`}
                  >
                    {isCorrect
                      ? 'Correto! Muito bem!'
                      : `Incorreto. A resposta certa é: "${current?.options.find((o) => o.isCorrect)?.text}"`}
                  </p>
                  {current?.explanation && (
                    <p className="text-sm text-muted-foreground mt-1">{current.explanation}</p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  )
}

export default Quiz
